<template>
  <BulkActionsBar
    v-if="canUpdateAssets || canDeleteAssets"
    :countSelected="countSelected"
    @clear:selection="handleClearSelection"
  >
    <template v-if="canUpdateAssets" #actions>
      <ActionsModal
        :steps="steps"
        :buildingIds="linkedBuildingIds"
        :hints="hints"
        @submit="handleSubmit"
      >
        <ActionsButton :disabled="!hasActions" />
      </ActionsModal>
    </template>
    <template #quick-actions>
      <DeleteModal
        v-if="canDeleteAssets"
        :title="$t('components.modals.delete.asset.title', 2)"
        :description="$t('components.modals.delete.asset.description', 2)"
        :hints="$t('components.modals.delete.asset.hints', 2).split('\\n')"
        @delete="handleDeleteAssets"
      >
        <DeleteButton />
      </DeleteModal>
    </template>
  </BulkActionsBar>
</template>

<script setup>
import axios from "axios";
import route from "ziggy-js";
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import {
  ActionsButton,
  ActionsModal,
  BulkActionsBar,
  DeleteButton,
  ActionType,
} from "@/SharedComponents/BulkActions";
import { DeleteModal } from "@/SharedComponents/Modals";
import { toast } from "@/SharedComponents/Toaster";
import { useBulkActions } from "@/Composables/useBulkActions";
import { useI18n } from "vue-i18n";

const props = defineProps({
  selectedAssets: { type: Array, default: () => [] },
});

const linkedBuildingIds = computed(() => {
  const buildingIds = props.selectedAssets.map((asset) => {
    if (asset.tenant) {
      return asset.tenant.id;
    }
    return asset.spaces[0].tenant_id;
  });
  return Array.from(new Set(buildingIds));
});

const useBulkActionsStore = useBulkActions(
  "assets",
  linkedBuildingIds,
  computed(() => props.selectedAssets)
);
const { countSelected, hasActions, steps, hints } =
  storeToRefs(useBulkActionsStore);
const { handleClearSelection } = useBulkActionsStore;

const { hasPermission, permissions } = inject("permissions");

const canUpdateAssets = computed(() =>
  linkedBuildingIds.value.every((buildingId) =>
    hasPermission(permissions.updateAsset, buildingId)
  )
);

const canDeleteAssets = computed(() =>
  linkedBuildingIds.value.every((buildingId) =>
    hasPermission(permissions.deleteAsset, buildingId)
  )
);

const { t } = useI18n();
const emitter = inject("emitter");
const busy = ref(false);

const selectedAssetIds = computed(() =>
  props.selectedAssets.map((asset) => asset.id)
);

const routes = {
  [ActionType.CATEGORY]: "api.assets.categorize",
  [ActionType.ASSET_GROUPS]: "api.assets.group",
  [ActionType.TAGS]: "api.assets.tag",
};

function handleSubmit({ type, payload }) {
  if (busy.value || selectedAssetIds.value.length === 0) {
    return;
  }

  busy.value = true;
  axios
    .post(route(routes[type]), {
      asset_ids: selectedAssetIds.value,
      ...payload,
    })
    .then((res) => {
      emitter.emit("update-assets-data", res.data);
      toast.success(t("toasts.assets.update.success", 2));
    })
    .catch((err) => {
      if (err.response.status == 403) {
        toast.error(t("toasts.assets.update.403", 2));
      } else {
        toast.error(
          err.response.data.message || t("toasts.assets.update.error", 2)
        );
      }
      emitter.emit("reload-assets");
    })
    .finally(() => {
      busy.value = false;
    });
}

function handleDeleteAssets() {
  if (busy.value || selectedAssetIds.value.length === 0) {
    return;
  }

  busy.value = true;
  axios
    .delete(route("api.assets.destroy"), {
      data: {
        asset_ids: selectedAssetIds.value,
      },
    })
    .then((res) => {
      emitter.emit("remove-assets-data", { data: res.data });
      handleClearSelection();
      toast.success(t("toasts.assets.delete.success", 2));
    })
    .catch((err) => {
      if (err.response.status == 403) {
        toast.error(t("toasts.assets.delete.403", 2));
      } else {
        toast.error(t("toasts.assets.delete.error", 2));
      }
      emitter.emit("reload-assets");
    })
    .finally(() => {
      busy.value = false;
    });
}
</script>
